import React, { useEffect, useRef, useState } from 'react';
import { readAuthorSetupIdentity } from '../../auth/authorSetup';
import config from '../../services/publishingGroupConfig.json';

export default function ReaderAppreciationSettings() {
  const [enabled, setEnabled] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [saved, setSaved] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [loading, setLoading] = useState(true);
  const owner = useRef(null);
  const lock = useRef(false);
  const generation = useRef(0);
  const dirty = loaded && enabled !== saved;
  async function request(method, identity, body) {
    const response = await fetch(`${config.apiBase}/author-appreciation`, {
      method, headers: { Authorization: `Bearer ${identity.token}`, 'Content-Type': 'application/json' }, signal: AbortSignal.timeout(20000),
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data?.error || 'Unable to update Reader Appreciation.');
    if (typeof data?.enabled !== 'boolean') throw new Error('Unable to read your Reader Appreciation preference. Please retry.');
    return data;
  }
  useEffect(() => {
    const current = ++generation.current;
    setLoading(true); setLoaded(false); setMessage('');
    readAuthorSetupIdentity().then(async identity => ({ identity, data: await request('GET', identity) }))
      .then(({ identity, data }) => { if (generation.current === current) { owner.current = identity.userSub; setEnabled(data.enabled); setSaved(data.enabled); setLoaded(true); } })
      .catch(() => { if (generation.current === current) setMessage('Unable to load Reader Appreciation. Please retry.'); })
      .finally(() => { if (generation.current === current) setLoading(false); });
    return () => { generation.current = current + 1; };
  }, [attempt]);
  useEffect(() => {
    const unload = event => { if (dirty || lock.current) { event.preventDefault(); event.returnValue = ''; } };
    const leave = event => { if (lock.current || (dirty && !window.confirm('Discard your unsaved Reader Appreciation changes?'))) event.preventDefault(); };
    window.addEventListener('beforeunload', unload);
    window.addEventListener('bookish:before-navigation', leave);
    return () => { window.removeEventListener('beforeunload', unload); window.removeEventListener('bookish:before-navigation', leave); };
  }, [dirty]);
  async function save() {
    if (!loaded || !dirty || lock.current) return;
    lock.current = true;
    const current = generation.current;
    setBusy(true); setMessage('');
    try {
      const identity = await readAuthorSetupIdentity();
      if (identity.userSub !== owner.current) throw new Error('Your Author account changed. Reopen Membership & Billing before saving.');
      const data = await request('PUT', identity, { enabled });
      if (current === generation.current) { setEnabled(data.enabled); setSaved(data.enabled); setMessage('Reader Appreciation preferences saved.'); }
    }
    catch (error) { if (current === generation.current) setMessage(error.message || 'Unable to save. Please retry.'); }
    finally { lock.current = false; if (current === generation.current) setBusy(false); }
  }
  return <section className="rounded-xl border border-bookish-purple/20 p-5 space-y-3">
    <h3 className="text-xl font-serif font-bold">Reader Appreciation Program</h3>
    <p>Participation is optional for Circle Authors. Keep entering your normal retail prices; Bookish Circle automatically applies member discounts.</p>
    <p><a href="/reader-appreciation" target="_blank" rel="noopener noreferrer" className="font-semibold underline text-bookish-purple">Learn more<span className="sr-only"> about the Reader Appreciation Program</span> <span className="text-sm font-normal">(opens in a new tab)</span></a></p>
    <label className="flex gap-3 min-h-[44px] items-start"><input type="checkbox" className="mt-1" checked={enabled} disabled={!loaded || busy} onChange={e => { setEnabled(e.target.checked); setMessage(''); }} />
      I agree to offer Circle Readers $1 off ebooks priced $3 or more and 10% off physical products.</label>
    <p className="text-sm">You can leave the program here at any time. Discounts apply to product prices, before shipping.</p>
    <button type="button" disabled={!loaded || busy || !dirty} onClick={save} className="min-h-[44px] rounded bg-bookish-purple px-4 py-2 text-white disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4">{busy ? 'Saving…' : 'Save participation'}</button>
    {!loaded && !loading && <button type="button" onClick={() => setAttempt(value => value + 1)} className="ml-3 min-h-[44px] underline">Retry loading preference</button>}
    <p role="status">{loading ? 'Loading your participation preference…' : message || (dirty ? 'You have unsaved participation changes.' : '')}</p>
  </section>;
}
